import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Table, TableHead, TableBody, TableRow, TableCell,
  Snackbar, Alert, Chip
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StoreIcon from '@mui/icons-material/Store';
import GroupIcon from '@mui/icons-material/Group';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function formatBRL(value) {
  return (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateBR(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

const STATUS_COLORS = {
  'Em Andamento': 'info',
  'Finalizado': 'success',
  'Reservado': 'warning',
  'Cancelado': 'error',
  'Pendente': 'default',
  'Pago': 'success',
};

function StatCard({ label, value, icon, iconBg, borderColor, href }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Paper
        component={href ? 'a' : 'div'}
        href={href}
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: '1px solid #efefef',
          borderLeft: `4px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          textDecoration: 'none',
          bgcolor: '#fff',
          transition: 'box-shadow 0.2s, transform 0.2s',
          cursor: href ? 'pointer' : 'default',
          '&:hover': href
            ? { boxShadow: '0 4px 20px rgba(0,0,0,0.09)', transform: 'translateY(-2px)' }
            : {},
        }}
      >
        <Box sx={{
          width: 50, height: 50, borderRadius: 1.5,
          bgcolor: iconBg, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" sx={{
            color: '#888', textTransform: 'uppercase',
            letterSpacing: 0.6, fontWeight: 700, display: 'block', fontSize: '0.68rem',
          }}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1, color: '#1a1a1a' }}>
            {value}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );
}

function FinCard({ label, value, bg, color }) {
  return (
    <Grid item xs={6} sm={3}>
      <Paper elevation={0} sx={{
        p: 2, borderRadius: 2, bgcolor: bg,
        border: '1px solid', borderColor: color + '30', textAlign: 'center',
      }}>
        <Typography variant="caption" sx={{
          color: '#666', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.5, fontSize: '0.65rem', display: 'block',
        }}>
          {label}
        </Typography>
        <Typography fontWeight={700} sx={{
          color, mt: 0.5,
          fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
        }}>
          {value}
        </Typography>
      </Paper>
    </Grid>
  );
}

export default function Dashboard() {
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [alugueis, setAlugueis] = useState([]);
  const [promocoes, setPromocoes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, mensagem: '', tipo: 'success' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    async function fetchData() {
      try {
        const [resC, resV, resA, resP, resF, resU] = await Promise.all([
          axios.get(`${API_URL}/clientes`),
          axios.get(`${API_URL}/veiculos`),
          axios.get(`${API_URL}/alugueis`),
          axios.get(`${API_URL}/promocoes`),
          axios.get(`${API_URL}/fornecedores`),
          axios.get(`${API_URL}/usuarios`),
        ]);
        setClientes(resC.data);
        setVeiculos(resV.data);
        setAlugueis(resA.data.slice(0, 35));
        setPromocoes(resP.data);
        setFornecedores(resF.data);
        setUsuarios(resU.data);
      } catch {
        setSnackbar({ open: true, mensagem: 'Erro ao carregar dados do dashboard.', tipo: 'error' });
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const agora = new Date();
  const limite48h = new Date(agora.getTime() - 48 * 60 * 60 * 1000);
  const alugueisUltimas48h = alugueis.filter(a => {
    const retirada = a.data_retirada ? new Date(a.data_retirada) : null;
    return retirada && retirada >= limite48h;
  });

  const soma = (statusAluguel, statusPagamento) =>
    alugueisUltimas48h
      .filter(a =>
        (!statusAluguel || a.status_aluguel === statusAluguel) &&
        (!statusPagamento || a.status_pagamento === statusPagamento)
      )
      .reduce((acc, a) => acc + Number(a.valor_total || a.valor || 0), 0);

  const hoje = agora.toLocaleDateString('pt-BR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  if (loading) return <Typography sx={{ p: 3 }}>Carregando dashboard...</Typography>;

  return (
    <Box sx={{ width: '100%' }}>

      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)',
        borderRadius: 2, p: 3, mb: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#f78b00' }}>
            📊 Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5, textTransform: 'capitalize' }}>
            {hoje}
          </Typography>
        </Box>
      </Box>

      {/* KPI cards — 6 em um único grid, 3 por linha */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard
          label="Veículos"
          value={veiculos.length}
          borderColor="#f78b00"
          iconBg="rgba(247,139,0,0.1)"
          icon={<DirectionsCarIcon sx={{ color: '#f78b00' }} />}
          href="/frotas"
        />
        <StatCard
          label="Clientes"
          value={clientes.length}
          borderColor="#1976d2"
          iconBg="rgba(25,118,210,0.1)"
          icon={<PeopleAltIcon sx={{ color: '#1976d2' }} />}
          href="/cadastros/clientes"
        />
        <StatCard
          label="Aluguéis"
          value={alugueis.length}
          borderColor="#43a047"
          iconBg="rgba(67,160,71,0.1)"
          icon={<AssignmentIcon sx={{ color: '#43a047' }} />}
          href="/alugueis"
        />
        <StatCard
          label="Promoções"
          value={promocoes.length}
          borderColor="#ff9800"
          iconBg="rgba(255,152,0,0.1)"
          icon={<LocalOfferIcon sx={{ color: '#ff9800' }} />}
        />
        <StatCard
          label="Fornecedores"
          value={fornecedores.length}
          borderColor="#0288d1"
          iconBg="rgba(2,136,209,0.1)"
          icon={<StoreIcon sx={{ color: '#0288d1' }} />}
        />
        <StatCard
          label="Usuários"
          value={usuarios.length}
          borderColor="#8e24aa"
          iconBg="rgba(142,36,170,0.1)"
          icon={<GroupIcon sx={{ color: '#8e24aa' }} />}
        />
      </Grid>

      {/* Resumo Financeiro */}
      <Paper elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUpIcon sx={{ color: '#f78b00', fontSize: 18 }} />
          <Typography variant="caption" fontWeight={700} sx={{
            color: '#555', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.72rem',
          }}>
            Resumo Financeiro — Últimas 48h
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          <FinCard label="Em Andamento"     value={formatBRL(soma('Em Andamento'))} bg="#e3f2fd" color="#1565c0" />
          <FinCard label="Pagos"            value={formatBRL(soma(null, 'Pago'))}   bg="#e8f5e9" color="#2e7d32" />
          <FinCard label="Finaliz. / Pagos" value={formatBRL(soma('Finalizado', 'Pago'))} bg="#fff8e1" color="#e65100" />
          <FinCard label="Pendentes"        value={formatBRL(soma(null, 'Pendente'))} bg="#ffebee" color="#c62828" />
        </Grid>
      </Paper>

      {/* Tabela de aluguéis */}
      <Paper elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          px: 2.5, py: 2,
          borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a1a1a' }}>
            Últimos Aluguéis
          </Typography>
          <Typography variant="caption" sx={{ color: '#bbb' }}>
            {alugueis.length} registros
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['ID', 'Cliente', 'Veículo', 'Retirada', 'Devolução', 'Status', 'Valor'].map(col => (
                  <TableCell key={col} sx={{
                    bgcolor: '#fafafa',
                    fontWeight: 700, fontSize: '0.7rem',
                    color: '#888', textTransform: 'uppercase', letterSpacing: 0.4,
                    py: 1.5, borderBottom: '2px solid #f0f0f0',
                  }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {alugueis.map((a) => {
                const cliente = clientes.find(c => c.id === a.id_cliente || c.uuid_cliente === a.id_cliente);
                const veiculo = veiculos.find(v => v.id === a.id_veiculo);
                const status = a.status_aluguel || '—';
                return (
                  <TableRow key={a.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                    <TableCell sx={{ color: '#bbb', fontSize: '0.78rem', fontWeight: 500 }}>
                      #{a.id}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {cliente?.nome || a.nome_cliente || '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#555', fontSize: '0.83rem' }}>
                      {veiculo ? `${veiculo.modelo} · ${veiculo.placa}` : a.modelo || '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#777', fontSize: '0.82rem' }}>
                      {formatDateBR(a.data_retirada)}
                    </TableCell>
                    <TableCell sx={{ color: '#777', fontSize: '0.82rem' }}>
                      {formatDateBR(a.data_devolucao)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status}
                        color={STATUS_COLORS[status] || 'default'}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.85rem' }}>
                      {formatBRL(Number(a.valor_total || a.valor || 0))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.tipo} sx={{ width: '100%' }}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.mensagem}
        </Alert>
      </Snackbar>
    </Box>
  );
}
